import React, {useMemo, useState} from 'react';

export const REVIEW_FILTERS = [
  {key: 'all', label: 'All'},
  {key: 'unanswered', label: 'Awaiting Response'},
  {key: 'positive', label: 'Positive'},
  {key: 'negative', label: 'Negative'},
];

function ratingBadge(rating) {
  if (rating === 1) {
    return <span className="seller-reviews__badge seller-reviews__badge--positive">Recommends</span>;
  }
  if (rating === 0) {
    return (
      <span className="seller-reviews__badge seller-reviews__badge--negative">
        Does not recommend
      </span>
    );
  }
  return <span className="seller-reviews__badge seller-reviews__badge--neutral">No rating</span>;
}

function snippet(text) {
  if (!text) return <span className="seller-reviews__snippet">No comment</span>;
  const trimmed = text.length > 90 ? `${text.slice(0, 90)}…` : text;
  return <span className="seller-reviews__snippet">{trimmed}</span>;
}

/**
 * Presentational table of inbound customer reviews. Search and filter state is
 * local; the parent owns data fetching and the detail modal.
 */
export default function ReviewsTable({reviews, onSelect}) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return reviews.filter(review => {
      if (filter === 'unanswered' && review.response) return false;
      if (filter === 'positive' && review.rating !== 1) return false;
      if (filter === 'negative' && review.rating !== 0) return false;
      if (!term) return true;
      const haystack = [
        review.comment,
        review.response,
        review.customer?.nickname,
        review.Product?.name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [reviews, filter, search]);

  return (
    <div className="seller-reviews__reviews">
      <div className="seller-reviews__toolbar">
        <div className="seller-reviews__filters" role="group" aria-label="Filter reviews">
          {REVIEW_FILTERS.map(option => (
            <button
              key={option.key}
              type="button"
              className={`seller-reviews__chip${filter === option.key ? ' is-active' : ''}`}
              aria-pressed={filter === option.key}
              onClick={() => setFilter(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          className="seller-reviews__search"
          placeholder="Search reviews…"
          value={search}
          onChange={event => setSearch(event.target.value)}
          aria-label="Search reviews"
        />
      </div>

      {visible.length === 0 ? (
        <p className="seller-reviews__empty">No reviews match your filters.</p>
      ) : (
        <table className="seller-reviews__table">
          <thead>
            <tr>
              <th scope="col">Rating</th>
              <th scope="col">Customer</th>
              <th scope="col">Product</th>
              <th scope="col">Comment</th>
              <th scope="col">Reply</th>
              <th scope="col"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {visible.map(review => (
              <tr
                key={review.uuid}
                className="seller-reviews__row"
                onClick={() => onSelect(review)}
              >
                <td>{ratingBadge(review.rating)}</td>
                <td>{review.customer?.nickname || 'Customer'}</td>
                <td>{review.Product?.name || '—'}</td>
                <td>{snippet(review.comment)}</td>
                <td>
                  {review.response ? (
                    <span className="seller-reviews__reply-indicator seller-reviews__reply-indicator--replied">
                      Replied
                    </span>
                  ) : (
                    <span className="seller-reviews__reply-indicator seller-reviews__reply-indicator--pending">
                      Awaiting
                    </span>
                  )}
                </td>
                <td>
                  <button
                    type="button"
                    className="button"
                    onClick={event => {
                      event.stopPropagation();
                      onSelect(review);
                    }}
                  >
                    View &amp; Respond
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
